/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Component, Host, h, Prop, State, Fragment, Event, EventEmitter, Watch, Method } from '@stencil/core';
import { HttpClient } from '../sew-ws-header/helpers/http-client';
import { LabelParser } from '../sew-ws-header/helpers/labels.helpers';
import { Labels } from '../sew-ws-header/types/labels.types';
import { CountrySelector } from '../sew-ws-header/types/language.types';
import {
    hasAddress,
    isHeadOfficeCountry,
    isLocationCountry,
    isRecommendedDealerCountry,
    isSalesPartnerCountry,
    isSalesServicePartnerCountry,
    isSupportedByCountry,
    isSupportedByPartnerCountry,
    mapCountry,
} from './sew-ws-local-switch.helper';
import { Country, Language, ResponseCountry } from './sew-ws-local-switch.types';
import { SelectOption } from '../../forms/sew-combobox/sew-combobox.types';
import { SewComboboxCustomEvent } from '../../../components';

interface DropdownItem {
    name: string;
    value: string;
}

@Component({
    tag: 'sew-ws-locale-switch',
    styleUrl: 'sew-ws-locale-switch.scss',
    shadow: false,
})
export class SewWsLocaleSwitch {
    @Event() switchLanguage: EventEmitter<string>;
    @Event() close: EventEmitter;

    @Prop({ attribute: 'countryCode' }) countryCode: string;
    @Prop({ attribute: 'languageCode' }) languageCode: string;
    @Prop({ attribute: 'countrySelectorUrl' }) countrySelectorUrl: string;
    @Prop({ attribute: 'labels' }) labels: Labels;
    @Prop({ attribute: 'httpClient' }) httpClient: HttpClient | null = null;
    @Prop({ attribute: 'isPreview' }) isPreview = false;
    @Prop({ attribute: 'basketHasItems' }) basketHasItems = false;

    @State() countries: Country[] | null = null;
    @State() selectedCountry: Country | null = null;
    @State() selectedLanguage: Language | null = null;
    @State() showErrorMessage = false;
    @State() labelParser: LabelParser;
    @State() selectableLanguages: DropdownItem[] = [];
    @State() countrySelectorError = false;

    @Method()
    async initCountries(): Promise<void> {
        this.fetchCountries();
    }

    @Watch('countrySelectorUrl')
    watchCountrySelectorUrl() {
        this.countries = null;
    }

    @Watch('labels')
    watchLabels(): void {
        this.labelParser = new LabelParser(this.labels);
    }

    private descriptionLabel = '';
    private selectedDomain = '';

    componentWillLoad(): void {
        this.labelParser = new LabelParser(this.labels);
    }

    private fetchCountries(): void {
        if (this.countries !== null) {
            return;
        }

        if (this.countrySelectorUrl) {
            fetch(this.countrySelectorUrl)
                .then((response: Response) => response.json())
                .then((response: ResponseCountry[]) => {
                    this.countries = response.map(mapCountry);
                })
                .then(() => {
                    this.setInitialCountry();
                    if (!this.isPreview) {
                        this.fetchLanguages(this.countryCode, this.languageCode);
                    } else {
                        // get selectable languages in preview from country_selector_json
                        // because we cannot send requests against WZ
                        if (isHeadOfficeCountry(this.selectedCountry)) {
                            const languages = this.selectedCountry.languages;
                            this.setSelectableLanguages(
                                { defaultLanguage: languages[0].code, languages, domain: '' },
                                this.languageCode
                            );
                        }
                    }
                })
                .catch(() => {
                    this.countrySelectorError = true;
                    if (!this.isPreview) {
                        this.fetchLanguages(this.countryCode, this.languageCode);
                    }
                });
        }
    }

    private countryChangeEvent(event: SewComboboxCustomEvent<SelectOption>): void {
        const value = event.detail.value;
        if (value) {
            this.selectedCountry = this.countries.find(
                country => country.code?.toLowerCase() === value?.toLowerCase()
            );
            this.showErrorMessage = this.selectedCountry.code !== this.countryCode;

            if (isHeadOfficeCountry(this.selectedCountry)) {
                this.descriptionLabel = '';
                if (this.httpClient) {
                    this.fetchLanguages(this.selectedCountry.code);

                    return;
                }

                this.selectedLanguage = this.selectedCountry.languages[0];
            } else if (isSupportedByCountry(this.selectedCountry)) {
                this.descriptionLabel = this.labelParser
                    .getLabel('CMS_HEADER_COUNTRY_SELECTION_SUPPORTED_BY')
                    .replace('${COUNTRY}', this.selectedCountry.name)
                    .replace(/\$\{SUPPORTED_BY_COUNTRY\}/g, this.selectedCountry.data.country);

                if (this.httpClient) {
                    this.fetchLanguages(this.selectedCountry.data.code);

                    return;
                }

                this.selectedLanguage = this.selectedCountry.data.languages[0];
            } else if (isSupportedByPartnerCountry(this.selectedCountry)) {
                this.descriptionLabel = this.labelParser
                    .getLabel('CMS_HEADER_COUNTRY_SELECTION_SUPPORTED_BY_PARTNER')
                    .replace('${COUNTRY}', this.selectedCountry.name)
                    .replace('${SUPPORTED_BY_COUNTRY}', this.selectedCountry.data.country);
            } else if (isLocationCountry(this.selectedCountry)) {
                this.descriptionLabel = this.labelParser
                    .getLabel('CMS_HEADER_COUNTRY_SELECTION_LOCATION')
                    .replace('${COUNTRY}', this.selectedCountry.name);
            } else if (isSalesPartnerCountry(this.selectedCountry)) {
                this.descriptionLabel = this.labelParser
                    .getLabel('CMS_HEADER_COUNTRY_SELECTION_SALES_PARTNER')
                    .replace('${COUNTRY}', this.selectedCountry.name);
            } else if (isSalesServicePartnerCountry(this.selectedCountry)) {
                this.descriptionLabel = this.labelParser
                    .getLabel('CMS_HEADER_COUNTRY_SELECTION_SALES_SERVICE_PARTNER')
                    .replace('${COUNTRY}', this.selectedCountry.name);
            } else if (isRecommendedDealerCountry(this.selectedCountry)) {
                this.descriptionLabel = this.labelParser
                    .getLabel('CMS_HEADER_COUNTRY_SELECTION_RECOMMENDED_DEALER')
                    .replace('${COUNTRY}', this.selectedCountry.name);
            }
        }
    }

    private fetchLanguages(countryCode: string, languageCode?: string) {
        // if possible we want to use languages from WZ, so we have a single point of truth and are able
        // to switch lanugages in applications even if the country selector json has errors
        if (this.httpClient) {
            this.httpClient.fetchLanguages(this.languageCode, countryCode).then(countrySelector => {
                this.selectedDomain = countrySelector.domain;
                this.setSelectableLanguages(countrySelector, languageCode ?? countrySelector.defaultLanguage);
            });
        }
    }

    private setInitialCountry() {
        if (this.countryCode && this.languageCode) {
            this.selectedCountry =
                this.countries.find(
                    country => country.code && country.code.toLowerCase() === this.countryCode
                ) ?? null;
        }
    }

    private languageChangeEvent(event: SewComboboxCustomEvent<SelectOption>): void {
        const value = event.detail.value;

        if (value) {
            this.setSelectedLanguage(value, this.selectedDomain);
        }
    }

    private setSelectableLanguages(countrySelector: CountrySelector, selectedLanguage: string) {
        this.selectableLanguages = countrySelector.languages.map(language => ({
            name: language.name,
            value: language.code,
        }));
        this.setSelectedLanguage(selectedLanguage, countrySelector.domain);
    }

    private setSelectedLanguage(value: string, domain: string) {
        value = value.toUpperCase();
        const language = this.selectableLanguages.find(
            language => language.value.toLocaleUpperCase() === value
        );

        this.selectedLanguage = language
            ? {
                  code: language.value,
                  name: language.name,
                  url: domain
                      ? `${domain}/${language.value.toLowerCase()}/technical_pages/forwarding/forwarding_start_page.html`
                      : '',
              }
            : null;
    }

    private changeToCountryWebsite = () => {
        if (this.countrySelectorError) {
            this.switchLanguage.emit(this.selectedLanguage.code);
            this.close.emit();

            return;
        }

        if (this.selectedCountry.code === this.countryCode) {
            this.switchLanguage.emit(this.selectedLanguage.code);
            this.close.emit();
        } else {
            window.location.href = this.selectedLanguage.url;
        }
    };

    private changeToPartnerWebsite = () => {
        if (hasAddress(this.selectedCountry)) {
            window.open(this.selectedCountry.data.address.url, '_blank').focus();
        }
    };

    private getLanguageSlectionElement() {
        return (
            <Fragment>
                <sew-combobox
                    ws
                    placeholder={this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_LANGUAGE')}
                    comboboxId="language-selection-combobox"
                    onItemSelected={(event: SewComboboxCustomEvent<SelectOption>) =>
                        this.languageChangeEvent(event)
                    }
                >
                    <select>
                        {this.selectableLanguages
                            .sort((a, b) =>
                                a.name.localeCompare(b.name, this.languageCode.replace('_', '-'), {
                                    sensitivity: 'base',
                                })
                            )
                            .map(option => (
                                <option
                                    value={option.value}
                                    selected={this.selectedLanguage?.code === option.value}
                                >
                                    {option.name}
                                </option>
                            ))}
                    </select>
                </sew-combobox>
                <sew-ws-button>
                    <button onClick={this.changeToCountryWebsite}>
                        {this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_SWITCH')}
                    </button>
                </sew-ws-button>
            </Fragment>
        );
    }

    render() {
        return (
            <Host>
                <div class="headline">
                    {this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_HEADLINE')}
                </div>
                {this.countries !== null && (
                    <sew-combobox
                        ws
                        placeholder={this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_COUNTRY_REGION')}
                        comboboxId="country-selection-combobox"
                        onItemSelected={(event: SewComboboxCustomEvent<SelectOption>) =>
                            this.countryChangeEvent(event)
                        }
                        limitMenuWidth
                    >
                        <select>
                            {this.countries
                                .sort((a, b) =>
                                    a.name.localeCompare(b.name, this.languageCode.replace('_', '-'), {
                                        sensitivity: 'base',
                                    })
                                )
                                .map(country => (
                                    <option
                                        value={country.code}
                                        selected={country.code === this.selectedCountry?.code}
                                        title={country.name}
                                    >
                                        {country.name}
                                    </option>
                                ))}
                        </select>
                    </sew-combobox>
                )}
                {this.selectedCountry && (
                    <Fragment>
                        <sew-ws-richtext>
                            {this.descriptionLabel.length > 0 && <p>{this.descriptionLabel}</p>}
                            {hasAddress(this.selectedCountry) && (
                                <p>
                                    <b>{this.selectedCountry.data.address.lines[0]}</b>
                                </p>
                            )}
                        </sew-ws-richtext>
                        {hasAddress(this.selectedCountry) && (
                            <sew-ws-show-more
                                text={this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_MORE_INFO')}
                                textMinify={this.labelParser.getLabel(
                                    'CMS_HEADER_COUNTRY_SELECTION_LESS_INFO'
                                )}
                            >
                                <ul>
                                    {this.selectedCountry.data.address.lines
                                        .filter((_, index) => index !== 0)
                                        .map(el => (
                                            <li>{el}</li>
                                        ))}
                                </ul>
                                <ul>
                                    {this.selectedCountry.data.address.tel && (
                                        <li>
                                            {this.labelParser.getLabel(
                                                'CMS_HEADER_COUNTRY_SELECTION_TELEPHONE'
                                            )}
                                            : {this.selectedCountry.data.address.tel}
                                        </li>
                                    )}
                                    {this.selectedCountry.data.address.fax && (
                                        <li>
                                            {this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_FAX')}:{' '}
                                            {this.selectedCountry.data.address.fax}
                                        </li>
                                    )}
                                    {this.selectedCountry.data.address.email && (
                                        <li>
                                            {this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_MAIL')}:{' '}
                                            {this.selectedCountry.data.address.email}
                                        </li>
                                    )}
                                </ul>
                            </sew-ws-show-more>
                        )}
                        {!hasAddress(this.selectedCountry) && this.getLanguageSlectionElement()}
                        {hasAddress(this.selectedCountry) && (
                            <sew-ws-button>
                                <button onClick={this.changeToPartnerWebsite}>
                                    {this.labelParser.getLabel(
                                        'CMS_HEADER_COUNTRY_SELECTION_OPEN_IN_NEW_WINDOW'
                                    )}
                                </button>
                            </sew-ws-button>
                        )}
                    </Fragment>
                )}

                {this.countrySelectorError && this.getLanguageSlectionElement()}

                {this.showErrorMessage && this.basketHasItems && (
                    <div class="error">
                        <sew-icon icon="warning-filled"></sew-icon>
                        <p>{this.labelParser.getLabel('CMS_HEADER_COUNTRY_SELECTION_ERROR_MESSAGE')}</p>
                    </div>
                )}
            </Host>
        );
    }
}
